import { ReactNode, useEffect, useState } from "react";
import { NavigateFunction } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import {
  createEnrichmentTask,
  createCompanyEnrichmentTask,
  createSchoolEnrichmentTask,
  createSearchTask,
  createSearchImport,
  getSearchImport,
  cancelSearchImport,
  SearchImportJob,
} from "@/services/workerApi";

type DialogKey = "enrichment" | "company_enrichment" | "school_enrichment" | "search";

function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

// Création manuelle de tâches (admin) : 4 formulaires migrés en Dialog shadcn,
// déclenchés par des boutons. Conserve le parsing d'URL LinkedIn de recherche.
export function CreateTaskDialogs({
  navigate,
  onCreated,
}: {
  navigate: NavigateFunction;
  onCreated: () => void;
}) {
  const [open, setOpen] = useState<DialogKey | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Enrichissement candidat
  const [candidateId, setCandidateId] = useState("");
  const [profileUrl, setProfileUrl] = useState("");
  const [campaignCandidateId, setCampaignCandidateId] = useState("");
  // Entreprise
  const [companyId, setCompanyId] = useState("");
  const [companyExternalRef, setCompanyExternalRef] = useState("");
  // École
  const [schoolId, setSchoolId] = useState("");
  const [schoolExternalRef, setSchoolExternalRef] = useState("");
  const [schoolProfileUrl, setSchoolProfileUrl] = useState("");
  // Recherche
  const [searchType, setSearchType] = useState<"profiles" | "schools" | "companies">("profiles");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMaxResults, setSearchMaxResults] = useState("100");
  const [searchCampaignId, setSearchCampaignId] = useState("");
  const [searchFilters, setSearchFilters] = useState<Record<string, unknown>>({});
  const [searchLinkedinUrl, setSearchLinkedinUrl] = useState("");
  // Import de recherche distribué (pages réparties + résilient + suivi)
  const [importJobId, setImportJobId] = useState<number | null>(null);
  const [importJob, setImportJob] = useState<SearchImportJob | null>(null);

  function resetAll() {
    setCandidateId("");
    setProfileUrl("");
    setCampaignCandidateId("");
    setCompanyId("");
    setCompanyExternalRef("");
    setSchoolId("");
    setSchoolExternalRef("");
    setSchoolProfileUrl("");
    setSearchType("profiles");
    setSearchQuery("");
    setSearchMaxResults("100");
    setSearchCampaignId("");
    setSearchFilters({});
    setSearchLinkedinUrl("");
    setImportJobId(null);
    setImportJob(null);
  }

  function close() {
    setOpen(null);
    resetAll();
  }

  function parseLinkedinSearchUrl(url: string) {
    try {
      const parsed = new URL(url);
      if (!parsed.hostname.includes("linkedin.com")) return;
      const path = parsed.pathname;
      if (path.includes("/people")) setSearchType("profiles");
      else if (path.includes("/companies")) setSearchType("companies");
      else if (path.includes("/schools")) setSearchType("schools");

      const keywords = parsed.searchParams.get("keywords");
      if (keywords) setSearchQuery(keywords);

      const filters: Record<string, unknown> = {};
      const filterKeys = [
        "geoUrn", "currentCompany", "pastCompany", "school", "industry",
        "network", "title", "firstName", "lastName", "profileLanguage",
        "serviceCategory", "connectionOf", "schoolFilter",
      ];
      for (const key of filterKeys) {
        const val = parsed.searchParams.get(key);
        if (val) {
          try {
            const decoded = JSON.parse(decodeURIComponent(val));
            filters[key] = decoded;
          } catch {
            filters[key] = val;
          }
        }
      }
      if (Object.keys(filters).length > 0) setSearchFilters(filters);
    } catch {
      // URL invalide → on ignore
    }
  }

  async function run(label: string, fn: () => Promise<unknown>) {
    setSubmitting(true);
    try {
      await fn();
      toast({ title: "Tâche créée", description: `${label} mise en file.` });
      close();
      onCreated();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Échec de la création",
        description: (e as Error)?.message ?? "Erreur inconnue",
      });
    } finally {
      setSubmitting(false);
    }
  }

  // Suivi de l'import distribué : poll toutes les 2s jusqu'à l'état terminal.
  useEffect(() => {
    if (importJobId == null) return;
    let active = true;
    const poll = async () => {
      try {
        const j = await getSearchImport(navigate, importJobId);
        if (!active) return;
        setImportJob(j);
        if (["completed", "failed", "cancelled"].includes(j.status) && interval) clearInterval(interval);
      } catch {
        /* poll non bloquant */
      }
    };
    void poll();
    const interval = setInterval(() => void poll(), 2000);
    return () => {
      active = false;
      if (interval) clearInterval(interval);
    };
  }, [importJobId, navigate]);

  async function onLaunchImport() {
    setSubmitting(true);
    try {
      const target = Math.min(parseInt(searchMaxResults, 10) || 1000, 1000);
      const { job_id } = await createSearchImport(navigate, {
        search_type: searchType,
        query: searchQuery,
        max_results: target,
        ...(Object.keys(searchFilters).length > 0 ? { filters: searchFilters } : {}),
        ...(searchCampaignId.trim() !== "" ? { campaign_id: parseInt(searchCampaignId, 10) } : {}),
      });
      setImportJob(null);
      setImportJobId(job_id);
      onCreated();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Échec de l'import",
        description: (e as Error)?.message ?? "Erreur inconnue",
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function onCancelImport() {
    if (importJobId == null) return;
    try {
      await cancelSearchImport(navigate, importJobId);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Échec de l'annulation",
        description: (e as Error)?.message ?? "Erreur inconnue",
      });
    }
  }

  const triggers: { key: DialogKey; label: string }[] = [
    { key: "enrichment", label: "Enrichissement candidat" },
    { key: "company_enrichment", label: "Enrichissement entreprise" },
    { key: "school_enrichment", label: "Enrichissement école" },
    { key: "search", label: "Tâche de recherche" },
  ];

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {triggers.map((t) => (
          <Button key={t.key} variant="outline" size="sm" onClick={() => setOpen(t.key)}>
            <Plus className="mr-1 h-4 w-4" />
            {t.label}
          </Button>
        ))}
      </div>

      <Dialog open={open !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          {open === "enrichment" && (
            <>
              <DialogHeader>
                <DialogTitle>Nouvelle tâche d'enrichissement</DialogTitle>
                <DialogDescription>Enrichit un profil candidat depuis LinkedIn.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <Field label="ID Candidat *">
                  <Input type="number" value={candidateId} onChange={(e) => setCandidateId(e.target.value)} />
                </Field>
                <Field label="URL du profil LinkedIn *">
                  <Input
                    type="url"
                    value={profileUrl}
                    onChange={(e) => setProfileUrl(e.target.value)}
                    placeholder="https://www.linkedin.com/in/..."
                  />
                </Field>
                <Field label="ID Campaign Candidate (optionnel)">
                  <Input
                    type="number"
                    value={campaignCandidateId}
                    onChange={(e) => setCampaignCandidateId(e.target.value)}
                  />
                </Field>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={close}>Annuler</Button>
                <Button
                  disabled={submitting || !candidateId || !profileUrl}
                  onClick={() =>
                    run("Enrichissement candidat", () =>
                      createEnrichmentTask(navigate, {
                        candidate_id: parseInt(candidateId, 10),
                        profile_url: profileUrl,
                        campaign_candidate_id: campaignCandidateId
                          ? parseInt(campaignCandidateId, 10)
                          : undefined,
                      }),
                    )
                  }
                >
                  Créer
                </Button>
              </DialogFooter>
            </>
          )}

          {open === "company_enrichment" && (
            <>
              <DialogHeader>
                <DialogTitle>Enrichissement entreprise</DialogTitle>
                <DialogDescription>Scrape la page LinkedIn d'une entreprise.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <Field label="ID Entreprise *">
                  <Input type="number" value={companyId} onChange={(e) => setCompanyId(e.target.value)} />
                </Field>
                <Field label="External Ref (URN LinkedIn) *">
                  <Input
                    type="text"
                    value={companyExternalRef}
                    onChange={(e) => setCompanyExternalRef(e.target.value)}
                    placeholder="urn:li:fsd_company:1234"
                  />
                </Field>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={close}>Annuler</Button>
                <Button
                  disabled={submitting || !companyId || !companyExternalRef}
                  onClick={() =>
                    run("Enrichissement entreprise", () =>
                      createCompanyEnrichmentTask(navigate, {
                        company_id: parseInt(companyId, 10),
                        company_external_ref: companyExternalRef,
                      }),
                    )
                  }
                >
                  Créer
                </Button>
              </DialogFooter>
            </>
          )}

          {open === "school_enrichment" && (
            <>
              <DialogHeader>
                <DialogTitle>Enrichissement école</DialogTitle>
                <DialogDescription>Scrape la page LinkedIn d'une école.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <Field label="ID École *">
                  <Input type="number" value={schoolId} onChange={(e) => setSchoolId(e.target.value)} />
                </Field>
                <Field label="External Ref (URN LinkedIn) *">
                  <Input
                    type="text"
                    value={schoolExternalRef}
                    onChange={(e) => setSchoolExternalRef(e.target.value)}
                    placeholder="urn:li:fsd_school:1234"
                  />
                </Field>
                <Field label="URL profil LinkedIn (optionnel)">
                  <Input
                    type="url"
                    value={schoolProfileUrl}
                    onChange={(e) => setSchoolProfileUrl(e.target.value)}
                    placeholder="https://www.linkedin.com/school/..."
                  />
                </Field>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={close}>Annuler</Button>
                <Button
                  disabled={submitting || !schoolId || !schoolExternalRef}
                  onClick={() =>
                    run("Enrichissement école", () =>
                      createSchoolEnrichmentTask(navigate, {
                        school_id: parseInt(schoolId, 10),
                        school_external_ref: schoolExternalRef,
                        school_profile_url: schoolProfileUrl || undefined,
                      }),
                    )
                  }
                >
                  Créer
                </Button>
              </DialogFooter>
            </>
          )}

          {open === "search" && importJobId === null && (
            <>
              <DialogHeader>
                <DialogTitle>Nouvelle tâche de recherche</DialogTitle>
                <DialogDescription>
                  Colle une URL de recherche LinkedIn pour pré-remplir les champs.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <Field label="URL LinkedIn (auto-remplit les champs)">
                  <Input
                    type="url"
                    value={searchLinkedinUrl}
                    onChange={(e) => {
                      setSearchLinkedinUrl(e.target.value);
                      parseLinkedinSearchUrl(e.target.value);
                    }}
                    placeholder="https://www.linkedin.com/search/results/people/?keywords=..."
                  />
                </Field>
                <Field label="Type de recherche *">
                  <Select value={searchType} onValueChange={(v) => setSearchType(v as typeof searchType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="profiles">Profils</SelectItem>
                      <SelectItem value="schools">Écoles</SelectItem>
                      <SelectItem value="companies">Entreprises</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Requête de recherche *">
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Ex : développeur python paris"
                  />
                </Field>
                <Field
                  label="Nombre max de résultats"
                  hint="Plafond LinkedIn ~1000/recherche (valeur bridée à 1000). L'import distribué répartit les pages entre plugins, gère les erreurs et fournit un suivi."
                >
                  <Input
                    type="number"
                    value={searchMaxResults}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "") {
                        setSearchMaxResults("");
                        return;
                      }
                      const n = parseInt(raw, 10);
                      if (Number.isNaN(n)) return;
                      setSearchMaxResults(String(Math.min(Math.max(n, 1), 1000)));
                    }}
                    min={1}
                    max={1000}
                  />
                </Field>
                <Field
                  label="ID Campagne (optionnel)"
                  hint="Si renseigné, les profils trouvés sont importés dans cette campagne ; sinon simplement mis en file d'enrichissement."
                >
                  <Input
                    type="number"
                    value={searchCampaignId}
                    onChange={(e) => setSearchCampaignId(e.target.value)}
                    placeholder="Ex : 42"
                    min={1}
                  />
                </Field>
                {Object.keys(searchFilters).length > 0 && (
                  <Field label="Filtres détectés">
                    <pre className="max-h-32 overflow-auto rounded border bg-muted/40 p-2 text-xs">
                      {JSON.stringify(searchFilters, null, 2)}
                    </pre>
                  </Field>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={close}>Annuler</Button>
                <Button
                  variant="secondary"
                  disabled={submitting || !searchQuery}
                  onClick={() => void onLaunchImport()}
                >
                  Import distribué (suivi)
                </Button>
                <Button
                  disabled={submitting || !searchQuery}
                  onClick={() =>
                    run("Tâche de recherche", () =>
                      createSearchTask(navigate, {
                        search_type: searchType,
                        query: searchQuery,
                        max_results: parseInt(searchMaxResults, 10) || 100,
                        ...(Object.keys(searchFilters).length > 0 ? { filters: searchFilters } : {}),
                        ...(searchCampaignId.trim() !== ""
                          ? { campaign_id: parseInt(searchCampaignId, 10) }
                          : {}),
                      }),
                    )
                  }
                >
                  Tâche brute (1 plugin)
                </Button>
              </DialogFooter>
            </>
          )}

          {open === "search" && importJobId !== null && (
            <>
              <DialogHeader>
                <DialogTitle>Import distribué #{importJob?.id ?? importJobId}</DialogTitle>
                <DialogDescription>
                  Pages réparties entre plugins, résilient aux erreurs. Plafond LinkedIn ~1000/recherche.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                {importJob ? (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Statut</span>
                      <span className="font-medium">{importJob.status}</span>
                    </div>
                    {(() => {
                      const total = importJob.chunks_total || 0;
                      const done = importJob.chunks_done + importJob.chunks_failed;
                      const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
                      return (
                        <div>
                          <div className="h-2 w-full overflow-hidden rounded bg-muted">
                            <div className="h-2 bg-primary transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {total > 0 ? `${done}/${total} pages (${pct}%)` : "Dimensionnement en cours…"}
                            {importJob.real_total != null ? ` · ${importJob.real_total} résultat(s) LinkedIn` : ""}
                          </p>
                        </div>
                      );
                    })()}
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="rounded border p-2">
                        <div className="text-xs text-muted-foreground">Importés</div>
                        <div className="font-semibold">{importJob.candidates_imported}</div>
                      </div>
                      <div className="rounded border p-2">
                        <div className="text-xs text-muted-foreground">Ignorés</div>
                        <div className="font-semibold">{importJob.candidates_skipped}</div>
                      </div>
                      <div className="rounded border p-2">
                        <div className="text-xs text-muted-foreground">Pages KO</div>
                        <div className="font-semibold">{importJob.chunks_failed}</div>
                      </div>
                    </div>
                    {importJob.error_message && (
                      <p className="text-sm text-destructive">{importJob.error_message}</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Chargement…</p>
                )}
              </div>
              <DialogFooter>
                {importJob && ["pending", "running", "cancelling"].includes(importJob.status) && (
                  <Button
                    variant="destructive"
                    disabled={importJob.cancel_requested || importJob.status === "cancelling"}
                    onClick={() => void onCancelImport()}
                  >
                    {importJob.cancel_requested || importJob.status === "cancelling"
                      ? "Annulation…"
                      : "Annuler l'import"}
                  </Button>
                )}
                <Button variant="outline" onClick={close}>Fermer</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
